import { useCallback, useEffect, useRef, useState } from "react";
import { loadCastSdk } from "./loadCastSdk";
import type {
  CastContext,
  CastStateValue,
  CastWindow,
  RemotePlayer,
  RemotePlayerController,
} from "./types";

export interface UseCastOptions {
  src?: string | null;
  poster?: string | null;
  title?: string | null;
  isLive?: boolean;
  /** Текущая позиция локального плеера — нужна только чтобы передать
   * стартовый сдвиг ресиверу при старте каста живого эфира (легасийный
   * `customData.offset`). Постоянная пересинхронизация во время каста
   * (перезагрузка media при перемотке эфира) сознательно не портируется,
   * см. обсуждение дизайна. */
  currentTime?: number;
}

export interface UseCastRemoteState {
  paused: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  muted: boolean;
  isLoading: boolean;
}

export interface UseCastResult {
  /** Показывать ли кнопку каста вообще (SDK загрузился, браузер
   * поддерживает, в сети есть хотя бы один Cast-приёмник). */
  isAvailable: boolean;
  /** Есть активная cast-сессия (не обязательно с этим src). */
  isConnected: boolean;
  /** Активная сессия транслирует именно этот src — в этом состоянии
   * Player подменяет локальное видео на CastOverlay. */
  isCastingThis: boolean;
  deviceName: string | null;
  remote: UseCastRemoteState;
  start: () => void;
  playOrPause: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  stop: () => void;
}

function getWin(): CastWindow | null {
  if (typeof window === "undefined") return null;
  const win = window as unknown as CastWindow;
  return win.cast?.framework ? win : null;
}

function endCurrentSession() {
  getWin()
    ?.cast.framework.CastContext.getInstance()
    .getCurrentSession()
    ?.endSession(true);
}

export function useCast({
  src,
  poster,
  title,
  isLive,
  currentTime,
}: UseCastOptions): UseCastResult {
  const [castState, setCastState] = useState<CastStateValue | null>(null);
  const [castingContentId, setCastingContentId] = useState<string | null>(null);
  const [deviceName, setDeviceName] = useState<string | null>(null);
  const [remote, setRemote] = useState<UseCastRemoteState>({
    paused: true,
    currentTime: 0,
    duration: 0,
    volume: 1,
    muted: false,
    isLoading: false,
  });

  const playerRef = useRef<RemotePlayer | null>(null);
  const controllerRef = useRef<RemotePlayerController | null>(null);
  const wasPlayingRef = useRef(false);
  const currentTimeRef = useRef(currentTime ?? 0);
  useEffect(() => {
    currentTimeRef.current = currentTime ?? 0;
  }, [currentTime]);

  // Загрузка SDK и подписка на состояние каста — один раз на монтирование
  // этого плеера. CastContext сам по себе singleton SDK, так что несколько
  // Player на разных страницах друг другу не мешают, но каждый должен снять
  // свои слушатели при размонтировании, иначе они копятся при навигации.
  useEffect(() => {
    let cancelled = false;
    let context: CastContext | null = null;
    let controller: RemotePlayerController | null = null;
    let onRemoteChange: (() => void) | null = null;
    let onCastStateChanged:
      | ((event: { castState: CastStateValue }) => void)
      | null = null;

    void loadCastSdk().then((available) => {
      if (cancelled || !available) return undefined;
      const win = getWin();
      if (!win) return undefined;

      // setOptions уже выставлен внутри loadCastSdk() — общий на страницу.
      context = win.cast.framework.CastContext.getInstance();

      const player = new win.cast.framework.RemotePlayer();
      controller = new win.cast.framework.RemotePlayerController(player);
      playerRef.current = player;
      controllerRef.current = controller;

      onRemoteChange = () => {
        // Каст доиграл до конца (или разорвался): останавливаем сессию,
        // иначе RemotePlayer зависает в IDLE, как в легаси. Легасийная
        // проверка конкретно idleReason === "FINISHED" здесь сознательно
        // упрощена: останавливаем сессию на любом IDLE после PLAYING
        // (включая CANCELLED/INTERRUPTED/ERROR) — не оставлять сессию
        // висеть в нерабочем состоянии в любом из этих случаев не хуже.
        if (player.playerState === "IDLE" && wasPlayingRef.current) {
          endCurrentSession();
        }
        wasPlayingRef.current = player.playerState === "PLAYING";
        // Читаем contentId прямо из RemotePlayer, а не только по
        // CAST_STATE_CHANGED: смена контента на уже открытой сессии
        // (loadMedia без новой сессии, см. start()) не порождает
        // CAST_STATE_CHANGED, только ANY_CHANGE — без этого другой
        // смонтированный Player с этим же хуком не узнал бы, что сессия
        // уехала на другой контент.
        setCastingContentId(player.mediaInfo?.contentId ?? null);
        setRemote({
          paused: player.isPaused,
          currentTime: player.currentTime || 0,
          duration: player.duration || 0,
          volume: player.volumeLevel ?? 1,
          muted: player.isMuted,
          isLoading: player.playerState === "BUFFERING",
        });
      };
      controller.addEventListener(
        win.cast.framework.RemotePlayerEventType.ANY_CHANGE,
        onRemoteChange,
      );

      const syncSession = () => {
        const session = context?.getCurrentSession() ?? null;
        setDeviceName(session?.getCastDevice()?.friendlyName ?? null);
        setCastingContentId(
          session?.getMediaSession()?.media?.contentId ?? null,
        );
      };
      onCastStateChanged = (event) => {
        setCastState(event.castState);
        syncSession();
      };
      context.addEventListener(
        win.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
        onCastStateChanged,
      );

      setCastState(context.getCastState());
      syncSession();
      return undefined;
    });

    return () => {
      cancelled = true;
      if (context && onCastStateChanged) {
        context.removeEventListener(
          getWin()?.cast.framework.CastContextEventType.CAST_STATE_CHANGED ??
            "",
          onCastStateChanged,
        );
      }
      if (controller && onRemoteChange) {
        controller.removeEventListener(
          getWin()?.cast.framework.RemotePlayerEventType.ANY_CHANGE ?? "",
          onRemoteChange,
        );
      }
    };
  }, []);

  const startingRef = useRef(false);

  const start = useCallback(async () => {
    if (!src || startingRef.current) return;
    const win = getWin();
    if (!win) return;
    const context = win.cast.framework.CastContext.getInstance();
    let session = context.getCurrentSession();

    // Уже кастуем именно этот ролик: не грузим заново, просто открываем
    // системное меню устройства/громкости — как в легаси, повторный
    // requestSession() на активной сессии не создаёт новую, а показывает
    // диалог управления текущей.
    if (session && castingContentId === src) {
      void context.requestSession();
      return;
    }

    startingRef.current = true;
    try {
      if (!session) {
        try {
          await context.requestSession();
        } catch {
          return; // пользователь закрыл системный диалог выбора устройства
        }
        session = context.getCurrentSession();
      }
      if (!session) return;

      const absoluteSrc = src.startsWith("http")
        ? src
        : `${window.location.origin}${src}`;
      const mediaInfo = new win.chrome.cast.media.MediaInfo(
        absoluteSrc,
        "application/x-mpegurl",
      );
      const metadata = new win.chrome.cast.media.GenericMediaMetadata();
      Object.assign(metadata, {
        title: title ?? "",
        images: poster ? [{ url: poster }] : undefined,
      });
      Object.assign(mediaInfo, {
        metadata,
        streamType: isLive
          ? win.chrome.cast.media.StreamType.LIVE
          : win.chrome.cast.media.StreamType.BUFFERED,
        duration: isLive ? -1 : undefined,
      });

      const request = new win.chrome.cast.media.LoadRequest(mediaInfo);
      Object.assign(request, {
        autoplay: true,
        currentTime: isLive ? currentTimeRef.current : undefined,
      });

      try {
        await session.loadMedia(request);
        setCastingContentId(src);
      } catch (error) {
        // Ресивер отказался грузить media (не тот MIME-тип, не достучался
        // до плейлиста, таймаут) — без этого промис повисал бы неотловленным,
        // а UI молча оставался в состоянии "не кастуем", хотя requestSession
        // уже отработал.
        console.error("Chromecast: failed to load media", error);
      }
    } finally {
      startingRef.current = false;
    }
  }, [src, title, poster, isLive, castingContentId]);

  const playOrPause = useCallback(() => {
    controllerRef.current?.playOrPause();
  }, []);

  const seek = useCallback((time: number) => {
    const player = playerRef.current;
    const controller = controllerRef.current;
    if (!player || !controller) return;
    player.currentTime = time;
    controller.seek();
  }, []);

  const setVolume = useCallback((volume: number) => {
    const player = playerRef.current;
    const controller = controllerRef.current;
    if (!player || !controller) return;
    player.volumeLevel = volume;
    controller.setVolumeLevel();
  }, []);

  const toggleMute = useCallback(() => {
    controllerRef.current?.muteOrUnmute();
  }, []);

  const isConnected = castState === "CONNECTED";
  const isCastingThis = isConnected && Boolean(src) && castingContentId === src;

  return {
    isAvailable: Boolean(castState) && castState !== "NO_DEVICES_AVAILABLE",
    isConnected,
    isCastingThis,
    deviceName,
    remote,
    start,
    playOrPause,
    seek,
    setVolume,
    toggleMute,
    stop: endCurrentSession,
  };
}
